import { z } from 'zod';
import { DomainError } from '../errors.js';
import type { ParseResult, SchemaIssue } from '../voyage/schemas.js';
import { DENSITY_MAX } from './bunkering.js';
import {
  type DecInput,
  decAdd,
  decCmp,
  decDiv,
  decMul,
  decRound,
  decSub,
  formatDec,
  parseDec,
} from './decimal.js';
import {
  optAmount,
  optEnum,
  optId,
  optLocalTime,
  optNote,
  optSigned,
  optText,
  requiredIssues,
  resultOf,
  typed,
} from './fields.js';

/**
 * ROB ölçümü (ADR 0117 §4, Ek Y4; ortak belgesi §10, ekran 06): tank başına sounding/ullage
 * okuması, sıcaklık, gözlenen hacim, 15 °C yoğunluğu ve ölçülen MT. Ölçüm **stok bakiyesini
 * değiştirmez**: yalnız karşılaştırılır; fark düzeltme talebiyle deftere girer. Ölçüm kaydı
 * değiştirilemez; yeniden ölçüm yeni kayıttır. Hacim düzeltmesi ASTM D1250 Tablo 54B (ürünler,
 * 15 °C) formülüyle; yöntem ve sürümü kayıtla saklanır. Elle girilen MT ve kalibrasyon tablosuz
 * okuma dayanak ister. Saf modül.
 */

export const SOUNDING_METHODS = ['sounding', 'ullage', 'sensor', 'manual'] as const;
export type SoundingMethod = (typeof SOUNDING_METHODS)[number];

/** Ölçümün kaynak belgesi (ekran 06 "Kaynak Rapor"). */
export const SOUNDING_SOURCES = [
  'ullage_report',
  'sounding_report',
  'bunker_survey',
  'voyage_report',
  'other',
] as const;
export type SoundingSource = (typeof SOUNDING_SOURCES)[number];

/**
 * Hacimden kütleye dönüşümün dayanağı: tankın kalibrasyon tablosu (sürümü kayıtta), sensör, ya
 * da elle (tablosuz okuma ya da elle MT; dayanak metni zorunlu).
 */
export const SOUNDING_CONVERSIONS = [
  'calibration_table',
  'sensor',
  'manual_volume',
  'manual',
] as const;
export type SoundingConversion = (typeof SOUNDING_CONVERSIONS)[number];

/** Hacim düzeltmesinin yöntemi ve sürümü (ölçümle saklanır; ADR 0117 §4). */
export const VCF_METHOD = 'ASTM_D1250_54B_1980';

export const SOUNDING_LINES_MAX = 40;

const lineSchema = z.strictObject({
  tankId: z.uuid('INVALID_ID'),
  method: optEnum(SOUNDING_METHODS),
  /** Sounding ya da ullage okuması (cm). */
  readingCm: optAmount({ max: '9999' }),
  tempC: optAmount({ max: '150' }),
  trimM: optSigned(),
  listDeg: optSigned(),
  /** Gözlenen hacim (m³, ölçüm sıcaklığında): kalibrasyon tablosundan ya da sensörden. */
  observedVolumeM3: optAmount(),
  density15: optAmount({ positive: true, max: DENSITY_MAX }),
  /** Yalnız elle yöntemde girilir; diğerlerinde hesaplanır. */
  massMt: optAmount(),
  basis: optNote(500),
});
export type SoundingLineInput = z.output<typeof lineSchema>;

export const fuelSoundingSchema = z.strictObject({
  measuredLocal: optLocalTime,
  sourceKind: optEnum(SOUNDING_SOURCES),
  sourceReference: optText(120),
  measuredById: optId,
  remarks: optNote(1000),
  /** Yeniden ölçümde önceki ölçüm (ortak belgesi §10 "Gerekirse Yeniden Ölç"). */
  replacesId: optId,
  lines: z
    .array(lineSchema, typed('INVALID_LIST'))
    .max(SOUNDING_LINES_MAX, 'TOO_LONG')
    .nullish()
    .transform((value) => value ?? []),
});
export type FuelSoundingInput = z.output<typeof fuelSoundingSchema>;

export function parseFuelSounding(input: unknown): ParseResult<FuelSoundingInput> {
  return resultOf(fuelSoundingSchema.safeParse(input));
}

const LINE_REQUIRED: Readonly<Record<SoundingMethod, readonly (keyof SoundingLineInput)[]>> = {
  sounding: ['readingCm', 'tempC', 'observedVolumeM3', 'density15'],
  ullage: ['readingCm', 'tempC', 'observedVolumeM3', 'density15'],
  sensor: ['tempC', 'observedVolumeM3', 'density15'],
  manual: ['massMt', 'basis'],
};

/**
 * Ölçümün yazma sorunları (ölçüm taslaksız, doğrudan değiştirilemez kayıttır): başlık zorunluları,
 * en az bir tank, yönteme göre satır zorunluları, aynı tank iki kez yok. Kalibrasyon tablosu
 * olmayan tankın sounding/ullage okuması dayanak ister (eksik dönüşüm dayanağı olmasın).
 */
export function soundingIssues(
  value: FuelSoundingInput,
  context: { calibratedTankIds: readonly string[] },
): SchemaIssue[] {
  const issues = requiredIssues(value, ['measuredLocal', 'sourceKind', 'measuredById', 'lines']);
  const seen = new Set<string>();
  value.lines.forEach((line, index) => {
    const at = (field: string) => `lines.${index}.${field}`;
    if (line.method === null) {
      issues.push({ path: at('method'), code: 'REQUIRED' });
    } else {
      for (const field of LINE_REQUIRED[line.method]) {
        if (line[field] === null) issues.push({ path: at(field), code: 'REQUIRED' });
      }
      const tableless =
        (line.method === 'sounding' || line.method === 'ullage') &&
        !context.calibratedTankIds.includes(line.tankId);
      if (tableless && line.basis === null) issues.push({ path: at('basis'), code: 'REQUIRED' });
    }
    if (seen.has(line.tankId)) issues.push({ path: at('tankId'), code: 'DUPLICATE' });
    seen.add(line.tankId);
  });
  return issues;
}

/**
 * ASTM D1250-80 Tablo 54B (genel ürünler, 15 °C): VCF = exp(−α·Δt·(1 + 0,8·α·Δt)), α = K0/ρ² +
 * K1/ρ (geçiş bölgesinde α = A + B/ρ²); ρ kg/m³ (0,1'e), Δt = t − 15 (t 0,1 °C'ye). Sonuç dört
 * ondalık (tablonun basamağı). Üstel fonksiyon standardın formülüdür: kayan nokta yalnız burada ve
 * sonuç tanımlı basamağa yuvarlanır; ölçüm miktarları ondalıktır (ADR 0117 §11). Aralık dışı
 * yoğunluk `DENSITY_OUT_OF_RANGE`.
 */
export function vcf54B(density15: DecInput, tempC: DecInput): string {
  const rho = Number(formatDec(decRound(decMul(density15, '1000', 6), 1)));
  const t = Number(formatDec(decRound(tempC, 1)));
  let alpha: number;
  if (rho >= 838.5 && rho < 1163.5) alpha = 186.9696 / rho ** 2 + 0.4862 / rho;
  else if (rho >= 787.5 && rho < 838.5) alpha = 594.5418 / rho ** 2;
  else if (rho >= 770.5 && rho < 787.5) alpha = -0.00336312 + 2680.3206 / rho ** 2;
  else if (rho >= 610.5 && rho < 770.5) alpha = 346.4228 / rho ** 2 + 0.4388 / rho;
  else throw new DomainError('DENSITY_OUT_OF_RANGE');
  const dt = t - 15;
  const vcf = Math.exp(-alpha * dt * (1 + 0.8 * alpha * dt));
  return formatDec(decRound(vcf.toFixed(8), 4));
}

/** Kalibrasyon tablosunun satırı: okuma (cm) → hacim (m³). */
export interface CalibrationRow {
  readingCm: string;
  volumeM3: string;
}

/** Tablodan doğrusal ara değerle hacim (m³, üç ondalık); tablo dışı okuma `null`. */
export function calibrationVolume(
  rows: readonly CalibrationRow[],
  readingCm: string,
): string | null {
  const sorted = [...rows].sort((a, b) => decCmp(a.readingCm, b.readingCm));
  for (let i = 0; i < sorted.length; i++) {
    const row = sorted[i] as CalibrationRow;
    if (decCmp(row.readingCm, readingCm) === 0) return formatDec(parseDec(row.volumeM3));
    const next = sorted[i + 1];
    if (!next) break;
    if (decCmp(row.readingCm, readingCm) < 0 && decCmp(readingCm, next.readingCm) < 0) {
      const share = decDiv(
        decSub(readingCm, row.readingCm),
        decSub(next.readingCm, row.readingCm),
        9,
      );
      const delta = decMul(decSub(next.volumeM3, row.volumeM3), share, 3);
      return formatDec(decRound(decAdd(row.volumeM3, delta), 3));
    }
  }
  return null;
}

export interface SoundingLineResult {
  conversion: SoundingConversion;
  vcf: string | null;
  /** 15 °C hacmi (m³) = gözlenen hacim × VCF. */
  correctedVolumeM3: string | null;
  /** Ölçülen MT = 15 °C hacmi × 15 °C yoğunluğu (kütle; ekran 06 "Ölçülen MT"). */
  massMt: string;
}

/**
 * Satırın sonucu: elle MT olduğu gibi; diğerlerinde VCF, 15 °C hacmi ve kütle (üç ondalık).
 * Sounding/ullage tabloluysa dönüşüm `calibration_table` (sürüm kayıtta), tablosuzsa
 * `manual_volume` (dayanakla).
 */
export function soundingLineResult(
  line: Pick<
    SoundingLineInput,
    'method' | 'observedVolumeM3' | 'tempC' | 'density15' | 'massMt' | 'basis'
  >,
  context: { calibrationVersionId: string | null },
): SoundingLineResult {
  if (line.method === 'manual') {
    if (line.massMt === null) throw new DomainError('INCOMPLETE_SOUNDING', 'massMt');
    return {
      conversion: 'manual',
      vcf: null,
      correctedVolumeM3: null,
      massMt: formatDec(line.massMt),
    };
  }
  if (line.observedVolumeM3 === null || line.tempC === null || line.density15 === null) {
    throw new DomainError('INCOMPLETE_SOUNDING', 'volume');
  }
  const vcf = vcf54B(line.density15, line.tempC);
  const corrected = decMul(line.observedVolumeM3, vcf, 3);
  return {
    conversion:
      line.method === 'sensor'
        ? 'sensor'
        : context.calibrationVersionId
          ? 'calibration_table'
          : 'manual_volume',
    vcf,
    correctedVolumeM3: formatDec(corrected),
    massMt: formatDec(decMul(corrected, line.density15, 3)),
  };
}
